import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const modelFormatsComponent: ComponentDef = {
  id: 'model-formats',
  name: 'Model Formats',
  description: 'Serialize models to Safetensors, ONNX, or GGUF for inference and deployment.',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    {
      name: 'variant',
      label: 'View',
      type: 'select',
      default: 'format_comparison',
      options: [
        { label: 'Format Comparison', value: 'format_comparison' },
        { label: 'Quantization Formats', value: 'quant_formats' },
        { label: 'Conversion Pipeline', value: 'conversion' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const variant = String(params.variant);

    const g = new GraphBuilder();
    g.input('weights', [d_model, d_model]);

    if (variant === 'format_comparison') {
      // Safetensors, ONNX, GGUF, PyTorch side by side
      g.split('formats', 'weights', [d_model, d_model], '4 formats');

      // Safetensors branch
      g.scale('safe_serial', 'formats', 'Serialize');
      g.scale('safe_meta', 'safe_serial', 'Safetensors Metadata');
      g.activation('safe_out', 'safe_meta', 'Safetensors');

      // ONNX branch
      g.scale('onnx_serial', 'formats', 'Serialize');
      g.scale('onnx_meta', 'onnx_serial', 'ONNX Metadata');
      g.activation('onnx_out', 'onnx_meta', 'ONNX');

      // GGUF branch
      g.scale('gguf_serial', 'formats', 'Serialize');
      g.scale('gguf_meta', 'gguf_serial', 'GGUF Metadata');
      g.activation('gguf_out', 'gguf_meta', 'GGUF');

      // PyTorch branch
      g.scale('pt_serial', 'formats', 'Serialize');
      g.scale('pt_meta', 'pt_serial', 'PyTorch Metadata');
      g.activation('pt_out', 'pt_meta', 'PyTorch');

      g.concat('compare', ['safe_out', 'onnx_out', 'gguf_out', 'pt_out'], [4, d_model]);
      g.scale('out', 'compare', 'Format Comparison');
    } else if (variant === 'quant_formats') {
      // FP16 -> quantize to INT8/INT4/GPTQ/AWQ -> per-format metadata -> output
      g.cast('fp16', 'weights', [d_model, d_model], 'FP16');
      g.split('quant_methods', 'fp16', [d_model, d_model], '4 quant methods');

      // INT8 branch
      g.quantize('int8', 'quant_methods', [d_model, d_model], 8);
      g.scale('int8_meta', 'int8', 'Scale + Zero-point');

      // INT4 branch
      g.quantize('int4', 'quant_methods', [d_model, d_model], 4);
      g.scale('int4_meta', 'int4', 'Scale + Zero-point');

      // GPTQ branch
      g.quantize('gptq', 'quant_methods', [d_model, d_model], 4);
      g.scale('gptq_meta', 'gptq', 'Group Size + Hessian');

      // AWQ branch
      g.quantize('awq', 'quant_methods', [d_model, d_model], 4);
      g.scale('awq_meta', 'awq', 'Activation-aware Scale');

      g.concat('compare', ['int8_meta', 'int4_meta', 'gptq_meta', 'awq_meta'], [4, d_model]);
      g.scale('out', 'compare', 'Quant Format Comparison');
    } else {
      // Conversion pipeline: source format -> load -> validate -> transform -> branch to target formats -> save
      g.scale('load', 'weights', 'Load Source');
      g.scale('validate', 'load', 'Validate');
      g.scale('rename', 'validate', 'Rename Layers');
      g.cast('cast', 'rename', [d_model, d_model], 'Cast Dtypes');
      g.split('targets', 'cast', [d_model, d_model], 'Target Formats');

      // Branch to multiple target formats
      g.activation('to_onnx', 'targets', 'Save ONNX');
      g.activation('to_gguf', 'targets', 'Save GGUF');
      g.activation('to_safe', 'targets', 'Save Safetensors');

      g.concat('out', ['to_onnx', 'to_gguf', 'to_safe'], [3, d_model]);
    }

    g.output('out', 'out');
    return g.build();
  }
};
